import React, { createContext, useContext, useEffect, useState } from 'react'

const LanguageContext = createContext()

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const initializeLanguage = async () => {
      // Check localStorage first (fastest)
      const savedLanguage = localStorage.getItem('userLanguage')
      
      if (savedLanguage && ['en', 'nl', 'de', 'ar'].includes(savedLanguage)) {
        console.log(`📦 Language loaded from storage: ${savedLanguage}`)
        setLanguage(savedLanguage)
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      console.log('🌍 Detecting location and language...')

      try {
        const detectedLanguage = await detectLanguageFromIPAPI()
        setLanguage(detectedLanguage)
        localStorage.setItem('userLanguage', detectedLanguage)
        console.log(`✅ Language detected: ${detectedLanguage}`)
      } catch (error1) {
        console.warn('⚠️ IP detection failed:', error1.message)
        
        try {
          // Fallback: Try navigator.language (browser locale)
          const browserLang = detectLanguageFromBrowserLocale()
          setLanguage(browserLang)
          localStorage.setItem('userLanguage', browserLang)
          console.log(`✅ Language detected from browser: ${browserLang}`)
        } catch (error2) {
          console.warn('⚠️ Browser locale detection failed:', error2.message)
          
          // Final fallback: English
          setLanguage('en')
          localStorage.setItem('userLanguage', 'en')
          console.log('🔄 Using default language: English')
        }
      } finally {
        setIsLoading(false)
      }
    }

    initializeLanguage()
  }, [])

  // ✅ FIXED: Use ipify.org API (more reliable)
  const detectLanguageFromIPAPI = async () => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Timeout (5s) - API not responding'))
      }, 5000)

      // Method 1: Try ipify.org (REST API - no JSONP needed)
      fetch('https://ipapi.co/json/')
        .then(response => {
          clearTimeout(timeout)
          if (!response.ok) throw new Error('API response error')
          return response.json()
        })
        .then(data => {
          if (data.country_code) {
            console.log(`🌍 IP detected from: ${data.country_name} (${data.country_code})`)
            const detectedLang = mapCountryToLanguage(data.country_code)
            resolve(detectedLang)
          } else {
            throw new Error('No country code in response')
          }
        })
        .catch(error => {
          clearTimeout(timeout)
          reject(new Error(`IP detection error: ${error.message}`))
        })
    })
  }

  const detectLanguageFromBrowserLocale = () => {
    const browserLang = navigator.language || navigator.userLanguage
    console.log(`🌐 Browser language: ${browserLang}`)
    
    const localeMap = {
      'nl': 'nl',
      'nl-NL': 'nl',
      'nl-BE': 'nl',
      'de': 'de',
      'de-DE': 'de',
      'de-AT': 'de',
      'de-CH': 'de',
      'ar': 'ar',
      'ar-AE': 'ar',
      'ar-SA': 'ar',
      'ar-EG': 'ar',
      'en': 'en',
      'en-US': 'en',
      'en-GB': 'en',
    }
    
    if (localeMap[browserLang]) {
      return localeMap[browserLang]
    }
    
    const lang = browserLang.split('-')[0]
    if (localeMap[lang]) {
      return localeMap[lang]
    }
    
    return 'en'
  }

  const mapCountryToLanguage = (country) => {
    const countryToLanguage = {
      // Dutch
      'NL': 'nl',
      'BE': 'nl',
      
      // German
      'DE': 'de',
      'AT': 'de',
      'CH': 'de',
      'LI': 'de',
      
      // Arabic
      'AE': 'ar',
      'SA': 'ar',
      'QA': 'ar',
      'KW': 'ar',
      'BH': 'ar',
      'OM': 'ar',
      'JO': 'ar',
      'LB': 'ar',
      'EG': 'ar',
      'MA': 'ar',
      'TN': 'ar',
      'DZ': 'ar',
      'SY': 'ar',
      'IQ': 'ar',
      'PS': 'ar',
      'YE': 'ar',
      'IR': 'ar',
      'SD': 'ar',
      'MR': 'ar',
      'SO': 'ar',
      'DJ': 'ar',
      'KM': 'ar',
    }

    const detected = countryToLanguage[country] || 'en'
    console.log(`🗺️ Country: ${country} → Language: ${detected}`)
    return detected
  }

  const changeLanguage = (lang) => {
    if (!lang || !['en', 'nl', 'de', 'ar'].includes(lang)) {
      console.warn(`Invalid language code: ${lang}`)
      return
    }
    setLanguage(lang)
    localStorage.setItem('userLanguage', lang)
    console.log(`📝 Language changed to: ${lang}`)
  }

  const value = {
    language,
    changeLanguage,
    isLoading,
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider')
  }
  return context
}

export default LanguageProvider